/**
 * UserDropdown Component (DC-900)
 *
 * Dropdown menu in the header showing user info and actions.
 * Includes avatar, name, role, and logout option.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export function UserDropdown() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    setIsOpen(false);
    navigate('/profile');
  };

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  // Get user initials for avatar
  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Role display names
  const roleLabels: Record<string, string> = {
    admin: 'Administrator',
    analyst: 'Analyst',
    viewer: 'Viewer',
  };

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button
        className="user-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">{initials}</div>
        <div className="user-info">
          <span className="user-name">{user.name}</span>
          <span className="user-role">{roleLabels[user.role] || user.role}</span>
        </div>
        <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem' }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu" role="menu">
          <div className="user-dropdown-header">
            <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{user.name}</div>
            <div className="user-dropdown-email">{user.email}</div>
            <div className="user-dropdown-org">{user.organization.name}</div>
          </div>

          <div className="user-dropdown-divider" />

          <button
            className="user-dropdown-item"
            onClick={handleProfile}
            role="menuitem"
          >
            <span>👤</span>
            Profile
          </button>

          <button
            className="user-dropdown-item"
            onClick={handleSettings}
            role="menuitem"
          >
            <span>⚙️</span>
            Settings
          </button>

          <div className="user-dropdown-divider" />

          <button
            className="user-dropdown-item danger"
            onClick={handleLogout}
            role="menuitem"
          >
            <span>🚪</span>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * UserAvatar Component - Just the avatar circle
 */
export function UserAvatar({ size = 32 }: { size?: number }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return null;
  }

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className="user-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
      }}
    >
      {initials}
    </div>
  );
}

/**
 * RoleBadge Component - Shows user role as a badge
 */
export function RoleBadge({ role }: { role: string }) {
  return <span className={`role-badge ${role}`}>{role}</span>;
}

export default UserDropdown;
